import React, { useCallback, useMemo, useState } from "react";
import classNames from "classnames";
import Modal from "@/ui/Modal/Modal";
import { ChevronIcon } from "@/assets/icons";
import {
  DateRange,
  PeriodPreset,
  StatisticsMode,
  useStatisticsPeriodStore,
} from "@/store/statisticsPeriodStore";
import {
  PERIOD_PRESETS,
  formatAllTimeShort,
  formatCustomRange,
  formatFull,
  formatRange,
  formatRangeTiny,
  getPresetRange,
  startOfDay,
} from "./periodDates";
import { PeriodCalendar } from "./PeriodCalendar";
import cls from "./styles.module.scss";
import type { Trigger } from "@/analytics";

const modes: { id: StatisticsMode; name: string }[] = [
  { id: "registration", name: "По регистрации" },
  { id: "completion", name: "По выполнению" },
];

const MODE_TRIGGERS: Record<StatisticsMode, Trigger> = {
  registration: "ref.period.mode_registration",
  completion: "ref.period.mode_completion",
};

interface PeriodOptionProps {
  name: string;
  subtitle?: string;
  isActive: boolean;
  onClick: () => void;
  trigger: Trigger;
}

const PeriodOption = ({
  name,
  subtitle,
  isActive,
  onClick,
  trigger,
}: PeriodOptionProps) => (
  <button
    type="button"
    data-trigger={trigger}
    className={classNames(cls.option, isActive ? cls.activeOption : undefined)}
    onClick={onClick}
  >
    <span>
      {name}
      {subtitle && <small>{subtitle}</small>}
    </span>
    <div className={cls.point}>{isActive && <i />}</div>
  </button>
);

interface PeriodSelectorProps {
  // дата первого реферала: ограничивает «Всё время» и календарь
  firstDate?: Date;
}

export const PeriodSelector = ({ firstDate }: PeriodSelectorProps) => {
  const preset = useStatisticsPeriodStore((s) => s.preset);
  const customRange = useStatisticsPeriodStore((s) => s.customRange);
  const draftRange = useStatisticsPeriodStore((s) => s.draftRange);
  const mode = useStatisticsPeriodStore((s) => s.mode);
  const setPreset = useStatisticsPeriodStore((s) => s.setPreset);
  const setCustomRange = useStatisticsPeriodStore((s) => s.setCustomRange);
  const setDraftRange = useStatisticsPeriodStore((s) => s.setDraftRange);
  const setMode = useStatisticsPeriodStore((s) => s.setMode);

  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState<"list" | "calendar">("list");

  const today = useMemo(() => startOfDay(new Date()), []);
  const minDate = firstDate ? startOfDay(firstDate) : undefined;

  const presetName = (id: PeriodPreset) =>
    PERIOD_PRESETS.find((p) => p.id === id)?.name ?? "";

  const presetSubtitle = (id: PeriodPreset) => {
    const range = getPresetRange(id, today, minDate);
    if (!range) return undefined;
    return id === "all" ? `с ${formatFull(range.from)}` : formatRange(range);
  };

  let value: string;
  if (customRange) {
    value = formatCustomRange(customRange);
  } else if (preset === "all") {
    value = formatAllTimeShort(minDate);
  } else {
    const range = getPresetRange(preset, today, minDate);
    value = range
      ? `${presetName(preset)} · ${formatRangeTiny(range)}`
      : presetName(preset);
  }

  const openSheet = () => {
    setView("list");
    setIsOpen(true);
  };

  const closeSheet = useCallback(() => setIsOpen(false), []);

  const handlePresetSelect = (id: PeriodPreset) => {
    setPreset(id);
    setIsOpen(false);
  };

  const handleCustomApply = (range: DateRange) => {
    setCustomRange(range);
    setIsOpen(false);
  };

  return (
    <>
      <div className={cls.card}>
        <button
          type="button"
          data-trigger="ref.period.open"
          className={cls.selector}
          onClick={openSheet}
        >
          <div>
            <div className={cls.selectorLabel}>ПЕРИОД</div>
            <div className={cls.selectorValue}>{value}</div>
          </div>
          <div className={cls.selectorIcon}>
            <ChevronIcon direction="down" />
          </div>
        </button>

        <div className={cls.modeSegment}>
          {modes.map(({ id, name }) => (
            <button
              key={id}
              type="button"
              data-trigger={MODE_TRIGGERS[id]}
              className={classNames(
                cls.modeButton,
                mode === id ? cls.activeMode : undefined
              )}
              onClick={() => setMode(id)}
            >
              {name}
            </button>
          ))}
        </div>
      </div>

      <Modal isOpen={isOpen} onClose={closeSheet}>
        <div className={cls.sheet}>
          <div className={cls.sheetTitle}>
            {view === "list" ? "Период" : "Свой период"}
          </div>

          {view === "list" ? (
            <>
              {PERIOD_PRESETS.map(({ id, name }) => {
                const subtitle = presetSubtitle(id);
                return (
                  <PeriodOption
                    key={id}
                    name={name}
                    subtitle={subtitle}
                    isActive={!customRange && preset === id}
                    onClick={() => handlePresetSelect(id)}
                    trigger="ref.period.preset"
                  />
                );
              })}
              <PeriodOption
                name="Свой период"
                subtitle="выбрать две даты в календаре"
                isActive={!!customRange}
                onClick={() => setView("calendar")}
                trigger="ref.period.custom"
              />
            </>
          ) : (
            <PeriodCalendar
              today={today}
              minDate={minDate}
              initialRange={draftRange ?? customRange}
              onApply={handleCustomApply}
              onChange={setDraftRange}
              onBack={() => setView("list")}
            />
          )}
        </div>
      </Modal>
    </>
  );
};
